import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  // Auth pages always render in dark mode
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 px-4 py-8 sm:py-12">
      <div className="w-full max-w-3xl mx-auto">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <Link to="/" className="text-3xl font-bold text-gray-900 dark:text-white hover:text-blue-500 dark:hover:text-blue-400 transition-colors" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            CREW FORGE
          </Link>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Privacy Policy
          </p>
        </div>

        {/* Back link */}
        <div className="mb-4">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            &larr; Back
          </button>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">CREW FORGE &mdash; Privacy Policy</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Last updated: 11 April 2026</p>

          <div className="space-y-6 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">

            {/* 1. Who We Are */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">1. Who We Are</h3>
              <p>
                Crew Forge is operated by <strong>Engineering Effects Ltd</strong> (Company No. pending registration), a company registered in England and Wales.
              </p>
              <p className="mt-2">
                We are the data controller for the purposes of UK GDPR.
              </p>
              <p className="mt-2">
                Contact:{' '}
                <a href="mailto:support@crewforge.co.uk" className="text-blue-600 dark:text-blue-400 hover:underline">
                  support@crewforge.co.uk
                </a>
              </p>
            </section>

            {/* 2. Data We Collect */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">2. Data We Collect</h3>
              <p>We may collect:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                <li>Name</li>
                <li>Email address</li>
                <li>Role / department</li>
                <li>Account activity</li>
                <li>Order and budgeting data</li>
                <li>Technical data (IP address, browser type, access logs)</li>
              </ul>
            </section>

            {/* 3. How We Use Data */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">3. How We Use Data</h3>
              <p>We process data to:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                <li>Provide the service</li>
                <li>Manage accounts</li>
                <li>Enable collaboration between users</li>
                <li>Ensure security</li>
                <li>Maintain audit logs</li>
                <li>Improve the platform</li>
              </ul>
            </section>

            {/* 4. Lawful Basis */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">4. Lawful Basis</h3>
              <p>We rely on:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                <li><strong>Contract performance</strong> &mdash; processing is necessary to provide the service you have agreed to use</li>
                <li><strong>Legitimate interests</strong> &mdash; running and improving the service</li>
              </ul>
            </section>

            {/* 5. Data Sharing */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">5. Data Sharing</h3>
              <p>We do not sell data.</p>
              <p className="mt-2">We may share data with:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                <li><strong>Hosting providers</strong> (Railway)</li>
                <li><strong>Email providers</strong> (Resend)</li>
                <li><strong>Error monitoring</strong> (Sentry &mdash; anonymous, no PII)</li>
              </ul>
              <p className="mt-2">All providers process data under contract.</p>
            </section>

            {/* 6. Data Retention */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">6. Data Retention</h3>
              <p>We retain data while accounts are active and for a reasonable period after.</p>
              <p className="mt-2">We may retain data longer for:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                <li>Legal obligations</li>
                <li>Financial records</li>
                <li>Dispute resolution</li>
                <li>Fraud prevention</li>
              </ul>
            </section>

            {/* 7. Your Rights */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">7. Your Rights</h3>
              <p>Under UK GDPR, you have the right to:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                <li>Access your data (Article 15)</li>
                <li>Correct your data (Article 16)</li>
                <li>Request deletion (Article 17 &mdash; see below)</li>
                <li>Data portability (Article 20)</li>
                <li>Restrict processing (Article 18)</li>
                <li>Object to processing (Article 21)</li>
              </ul>

              <h4 className="text-base font-semibold text-gray-900 dark:text-white mt-4 mb-1">
                Account deletion (Article 17)
              </h4>
              <p>
                To request deletion of your personal data, email{' '}
                <a href="mailto:support@crewforge.co.uk?subject=Account%20deletion%20request" className="text-blue-600 dark:text-blue-400 hover:underline">
                  support@crewforge.co.uk
                </a>{' '}
                with the subject &ldquo;Account deletion request&rdquo;. We will action your request within
                30 days of receipt, per UK GDPR Article 12(3).
              </p>
              <p className="mt-2">
                On deletion we scrub the following fields from our live systems: email,
                phone number, personal reference, and display name. Order records that
                reference your name as the requester are retained under the ICO
                legitimate-business-records exemption (UK GDPR Article 17(3)(b)) &mdash;
                necessary for VAT, audit, and dispute resolution per UK Companies Act 2006
                and HMRC record-keeping requirements. These retained records do not identify
                you by current contact details.
              </p>
              <p className="mt-2">
                You can export a copy of all your personal data at any time from
                <strong> My Settings &rarr; Your Data &rarr; Download My Data</strong>{' '}
                (UK GDPR Articles 15 and 20).
              </p>

              <h4 className="text-base font-semibold text-gray-900 dark:text-white mt-4 mb-1">
                Exercising other rights
              </h4>
              <p>
                For all other rights listed above, contact:{' '}
                <a href="mailto:support@crewforge.co.uk" className="text-blue-600 dark:text-blue-400 hover:underline">
                  support@crewforge.co.uk
                </a>
              </p>
              <p className="mt-2">
                You may also complain to the{' '}
                <a
                  href="https://ico.org.uk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Information Commissioner&rsquo;s Office (ICO)
                </a>{' '}
                at ico.org.uk.
              </p>
            </section>

            {/* 8. Security */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">8. Security</h3>
              <p>
                We use reasonable technical and organisational measures to protect data, including encrypted connections (HTTPS/TLS), secure session management, and access controls.
              </p>
            </section>

            {/* 9. Cookies */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">9. Cookies</h3>
              <p>We use essential cookies for:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                <li>Authentication</li>
                <li>Session management</li>
              </ul>
              <p className="mt-2">No tracking or marketing cookies are used. We also use localStorage for UI preferences (dark mode, pagination settings).</p>
            </section>

            {/* 10. Changes */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">10. Changes</h3>
              <p>
                We may update this policy. Material changes will be notified where reasonably possible. The latest version is always available on this page.
              </p>
            </section>
          </div>

          {/* Terms link */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              See also our{' '}
              <Link to="/terms-of-service" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
                Terms of Service
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          <Link to="/login" className="text-blue-600 dark:text-blue-400 hover:underline">
            Back to Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
